import {
	Checkbox,
	FormControlLabel,
	Grid,
	IconButton,
	Menu,
	MenuItem,
	Stack,
	Typography,
	MenuList,
} from '@mui/material';

import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { orderBy } from 'lodash';

import { ReactComponent as CrossIcon } from 'common/assets/images/cross.svg';
import { ReactComponent as FilterIcon } from 'common/assets/images/Filter.svg';
import { ReactComponent as AllCloseIcon } from 'common/assets/images/iconclose.svg';
import AccordionComp from 'common/components/Accordion/AccordionComp';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Pagination } from 'common/components/Table/CommonTableStyles';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { mappingCategory } from 'common/constants/TaxonomyConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setLandingPageCategoriesData,
	setLandingPageFilterData,
	setLandingPageTableData,
	setTaskRenderStatus,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { setCheckedItems } from 'common/store/reducer/horizonScanReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { updateData, getFilterValues } from 'features/HorizonScanning/api';
import { IFilterValueItem } from 'features/HorizonScanning/api/type-def';

import { handleAccordianChange } from './TasksListUtils';

import styles from './AdvanceFilterDropdown.module.scss';

interface FilterDropdownProps {
	sortChange?: any;
	pageChange?: any;
	selectedViewType?: Pagination;
	cleanTaskData?: () => void;
}

const FilterDropdown = (props: FilterDropdownProps) => {
	const { OK } = HTTP_STATUS_CODES;
	const { sortChange, selectedViewType, cleanTaskData } = props;
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedAccordians, setSelectedAccordians] = useState<string[]>([]);
	const [filterValues, setFilterValues] = useState<any>({});
	const [, setIsLoading] = useState(true);

	const searchText = useSelector(
		(state: RootState) => state.common.landingPageSearchText,
	);
	const filterData = useSelector(
		(state: RootState) => state.common.landingPageFilterData,
	);
	const categoriesData = useSelector(
		(state: RootState) => state.common.landingPageCategoriesData,
	);
	const [selectedCategories, setSelectedCategories] = useState<{
		[key: string]: string[];
	}>({});
	const [checkedItems, setCheckedItemsLocal] = useState<{
		[key: string]: boolean;
	}>(filterData);
	const { updateTableQuery } = useTableQuery({ id: 'HomePageTaskListTable' });
	const [isAnyCheckboxSelected, setIsAnyCheckboxSelected] = useState<
		boolean | null
	>(null);

	const viewTypeStatus = useSelector(
		(state: RootState) => state.common.taskListViewType,
	);

	const dispatch = useDispatch();
	const handleClearAll = () => {
		setSelectedCategories({});
		setCheckedItemsLocal({});
		dispatch(setLandingPageCategoriesData({}));
		const pageObj = {
			pageSize: selectedViewType?.pageSize || 10,
			pageNumber: 0,
		};
		updateTableQuery(pageObj);
		dispatch(setLandingPageFilterData({}));
		setSelectedAccordians([]);
	};
	const handleAccordianClose = () => {
		setSelectedAccordians([]);
	};
	const handleOpen = (event: any) => {
		setAnchorEl(event.currentTarget);
		setCheckedItems({ ...filterData });
	};

	const handleClose = () => {
		setCheckedItems({});
		setAnchorEl(null);
		setCheckedItemsLocal(filterData);
	};
	const { checkboxCheckedcolor, checkboxUncheckedcolor } = styles;
	const loggedInUser = useSelector(
		(state: RootState) => state?.common?.loggedInUser,
	);

	const listOfFilterValues = async () => {
		setIsLoading(true);
		const userId = loggedInUser?.userId;
		if (userId) {
			const res = await getFilterValues(userId);
			setIsLoading(false);
			if (res?.statusCode === OK) {
				const response = res?.responseBody;
				const sortedResponse: any = {};
				if (response) {
					Object.entries(response).forEach(([key, filterVal]) => {
						const sortedVal = orderBy(
							filterVal?.filter((e: IFilterValueItem) => !!e.value),
						);
						sortedResponse[key] = sortedVal;
					});
				}
				setFilterValues(sortedResponse);
			}
		} else {
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const updateTableFor = async (data: any) => {
		dispatch(setTaskRenderStatus(true));
		const res = await updateData(data);
		dispatch(setTaskRenderStatus(false));
		if (res?.statusCode === OK) {
			const response = res?.responseBody;
			dispatch(setLandingPageTableData(response));
		} else {
			if (cleanTaskData) {
				cleanTaskData();
			}
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};
	const handleEventFunc = (
		event: React.ChangeEvent<HTMLInputElement>,
		category: string,
		value: string,
	) => {
		const isChecked = event.target.checked;
		setCheckedItemsLocal((prevState) => ({
			...prevState,
			[value]: isChecked,
		}));

		setSelectedCategories((prevState) => ({
			...prevState,
			[category]: isChecked
				? [...(prevState[category] || []), value]
				: (prevState[category] || []).filter((item) => item !== value),
		}));
	};
	const handleCheckboxChange =
		(category: string, value: string) =>
		// eslint-disable-next-line indent
		(event: React.ChangeEvent<HTMLInputElement>) =>
			// eslint-disable-next-line indent
			handleEventFunc(event, category, value);
	useEffect(() => {
		setCheckedItemsLocal(filterData);
	}, [filterData]);

	useEffect(() => {
		setIsAnyCheckboxSelected(
			checkedItems ? Object.values(checkedItems).some((value) => value) : null,
		);
	}, [checkedItems]);
	const handleApply = () => {
		dispatch(setLandingPageCategoriesData(selectedCategories));
		if (isAnyCheckboxSelected) {
			const payload = constructPayload(sortChange);
			dispatch(setLandingPageFilterData(checkedItems));
			updateTableFor(payload);
			const pageObj = {
				pageSize: selectedViewType?.pageSize || 10,
				pageNumber: 0,
			};
			updateTableQuery(pageObj);
		}
		handleClose();
	};

	const constructPayload = (sortChange?: any) => {
		sortChange = sortChange || {};
		let { sortKey } = sortChange;
		if (sortKey === 'priorityLevel') {
			sortKey = 'taskImportance';
		} else if (sortKey === 'dueDate') {
			sortKey = 'targetEndDate';
		}
		const payload = {
			pageNumber: selectedViewType?.pageNumber || 0,
			pageSize: selectedViewType?.pageSize || 10,
			sortField: sortKey || 'targetEndDate',
			sortOrder: sortChange.sortDirection
				? sortChange.sortDirection.toUpperCase()
				: 'ASC',
			andCriterias: [] as { field: string; operation: string; value: any }[],
			orCriterias: [] as { field: string; operation: string; value: string }[],
		};
		payload.andCriterias.push({
			field: 'assignedUserId',
			operation: 'EQUAL',
			value: loggedInUser?.userId as string,
		});

		if (categoriesData) {
			Object.keys(categoriesData).forEach((category) => {
				if (categoriesData[category].length > 0) {
					payload.andCriterias.push({
						field: category,
						operation: 'INCLUDE',
						value: categoriesData[category],
					});
				}
			});
		}

		if (searchText) {
			const orCategories = [
				'taskType',
				'moduleName',
				'status',
				'taskImportance',
				'taskName',
			];
			orCategories.forEach((category) => {
				payload.orCriterias.push({
					field: category,
					operation: 'CONTAINS',
					value: searchText,
				});
			});
		}

		return payload;
	};

	useEffect(() => {
		const fetchData = async () => {
			await listOfFilterValues();
			const payload = constructPayload(sortChange);
			await updateTableFor(payload);
		};
		fetchData();
	}, [
		searchText,
		categoriesData,
		checkedItems,
		selectedViewType?.pageSize,
		selectedViewType?.pageNumber,
		sortChange,
		viewTypeStatus,
	]);

	return (
		<div>
			<IconButton
				aria-controls="filter-menu"
				aria-haspopup="true"
				onClick={handleOpen}
				data-testid="filter-icon"
			>
				<FilterIcon fill={checkboxCheckedcolor} />
			</IconButton>

			<Menu
				id="filter-menu"
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
			>
				<MenuList className={styles['advanced-filter-container']}>
					<MenuItem disableRipple>
						<Grid container alignItems="center" justifyContent="space-between">
							<Grid item display="flex" alignItems="center">
								<Typography
									className={styles['advancedfilters-header-title']}
									data-testid="advancedfilter-text"
								>
									{toSentenceCase('Advanced Filters')}
								</Typography>
							</Grid>
							<Grid
								item
								display="flex"
								alignItems="flex-end"
								gap="10px"
								data-testid="close-btn"
							>
								<CrossIcon onClick={handleClose} />
							</Grid>
						</Grid>
					</MenuItem>
					<hr />

					{!Object.entries(filterValues).length && (
						<MenuItem className={styles['msg-container']}>
							{toSentenceCase('No Data Found')}
						</MenuItem>
					)}

					{Object.entries(filterValues).length > 0 && (
						<>
							<MenuItem>
								<Grid container justifyContent="flex-end">
									<Grid item display="flex" alignItems="flex-end" gap="10px">
										<ButtonComp
											btnType="secondary"
											onClick={handleClearAll}
											data-testid="clear-all-button"
										>
											{toSentenceCase('Clear All')}
										</ButtonComp>
										<AllCloseIcon
											className={styles['advancedfilters-clearall-icon']}
											data-testid="advancedfilters-clearall-icon"
											onClick={handleAccordianClose}
										/>
									</Grid>
								</Grid>
							</MenuItem>

							{Object.entries(filterValues).map(([category, values]) => (
								<MenuItem key={category}>
									<AccordionComp
										className={styles['accordian-header']}
										dataTestId="filter-accordian"
										expanded={selectedAccordians.includes(category)}
										onChange={(event) =>
											handleAccordianChange(
												event,
												category,
												selectedAccordians,
												setSelectedAccordians,
											)
										}
										summary={
											<Typography data-testid="accordian-heading">
												{mappingCategory[category]}
											</Typography>
										}
										details={
											<Stack direction="column">
												{(values as IFilterValueItem[])
													.filter((value: IFilterValueItem) => !!value.value)
													.map((value: IFilterValueItem) => (
														<Stack
															direction="row"
															className={styles['filter-item']}
															key={value.value}
														>
															<FormControlLabel
																key={value.value}
																control={
																	<Checkbox
																		size="small"
																		data-testid="checkbox"
																		checked={checkedItems[value.value] || false}
																		onChange={handleCheckboxChange(
																			category,
																			value.value,
																		)}
																		sx={{
																			color: checkboxUncheckedcolor,
																			'&.Mui-checked': {
																				color: checkboxCheckedcolor,
																			},
																			'&.MuiCheckbox-indeterminate': {
																				color: checkboxCheckedcolor,
																			},
																		}}
																	/>
																}
																label={value.value}
															/>
															<Typography>{value.count}</Typography>
														</Stack>
													))}
											</Stack>
										}
									/>
								</MenuItem>
							))}

							<MenuItem>
								<Grid container justifyContent="flex-end">
									<Grid item display="flex" alignItems="flex-end" gap="10px">
										<ButtonComp
											btnType="secondary"
											data-testid="cancel-button"
											onClick={handleClose}
										>
											Cancel
										</ButtonComp>
										<ButtonComp
											btnType="primary"
											data-testid="apply-button"
											onClick={handleApply}
											disabled={!isAnyCheckboxSelected}
										>
											Apply
										</ButtonComp>
									</Grid>
								</Grid>
							</MenuItem>
						</>
					)}
				</MenuList>
			</Menu>
		</div>
	);
};
export default FilterDropdown;
